import React, { useRef, useState } from 'react';
import { Filter, Plus, X } from 'lucide-react';

export interface LeadFilters {
  status: string;
  meetingType: string;
  consultant: string;
  program: string;
  years: string[];
  createdFrom: string;
  createdTo: string;
}

interface SalesLeadFiltersProps {
  value: LeadFilters;
  onApply: (filters: LeadFilters) => void;
  profiles: { id: string; name: string; unit_type?: string }[];
  yearOptions: string[];
  showProgram: boolean;
}

const emptyFilters: LeadFilters = {
  status: 'all', meetingType: 'all', consultant: 'all', program: '',
  years: [], createdFrom: '', createdTo: '',
};

const filterNames = {
  status: 'Durum', consultant: 'Danışman', meetingType: 'Görüşme türü',
  date: 'Oluşturulma tarihi', program: 'Program', years: 'Kayıt yılı',
};
type FilterKey = keyof typeof filterNames;

const statusOptions = [
  ['new_lead', 'Yeni Lead'], ['enrolled', 'Kayıt'], ['rejected', 'Red'],
];
const meetingOptions = [
  ['video', 'Online Görüşme'], ['in-person', 'Fiziki Görüşme'], ['phone', 'Telefonda Görüşme'],
];
const programs = ['Tıp', 'Mühendislik', 'Mimarlık', 'Diş Hekimliği', 'Psikoloji', 'Hukuk', 'İşletme', 'Ekonomi', 'Uluslararası İlişkiler', 'Moda Tasarım', 'Müzik', 'Sanat'];

const activeKeys = (filters: LeadFilters): FilterKey[] => (
  Object.keys(filterNames) as FilterKey[]
).filter(key => {
  if (key === 'date') return Boolean(filters.createdFrom || filters.createdTo);
  if (key === 'years') return filters.years.length > 0;
  return Boolean(filters[key] && filters[key] !== 'all');
});

const clearFilter = (filters: LeadFilters, key: FilterKey): LeadFilters => {
  if (key === 'date') return { ...filters, createdFrom: '', createdTo: '' };
  if (key === 'years') return { ...filters, years: [] };
  return { ...filters, [key]: emptyFilters[key] };
};

const fieldClass = 'w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white';

const SalesLeadFilters: React.FC<SalesLeadFiltersProps> = ({ value, onApply, profiles, yearOptions, showProgram }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState(value);
  const [selected, setSelected] = useState<FilterKey[]>([]);
  const applied = activeKeys(value);

  const open = () => {
    setDraft({ ...value, years: [...value.years] });
    setSelected(activeKeys(value));
    dialogRef.current?.showModal();
  };

  const describe = (key: FilterKey) => {
    switch (key) {
      case 'status': return statusOptions.find(([id]) => id === value.status)?.[1] || value.status;
      case 'meetingType': return meetingOptions.find(([id]) => id === value.meetingType)?.[1] || value.meetingType;
      case 'consultant': return profiles.find(profile => profile.id === value.consultant)?.name || 'Seçili danışman';
      case 'program': return value.program;
      case 'years': return value.years.join(', ');
      case 'date': {
        const displayDate = (date: string) => date ? date.split('-').reverse().join('.') : '';
        return `${displayDate(value.createdFrom) || 'Başlangıç yok'} – ${displayDate(value.createdTo) || 'Bitiş yok'}`;
      }
    }
  };

  return (
    <>
      <button type="button" onClick={open} className="flex shrink-0 items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
        <Filter className="h-4 w-4" />
        Filtreler{applied.length > 0 && <span className="rounded-full bg-blue-100 px-2 text-sm text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{applied.length}</span>}
      </button>

      {applied.length > 0 && (
        <div className="flex w-full flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800" aria-label="Uygulanan filtreler">
          {applied.map(key => (
            <button key={key} type="button" onClick={() => onApply(clearFilter(value, key))} aria-label={`${filterNames[key]} filtresini kaldır: ${describe(key)}`} className="inline-flex max-w-full items-center gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50">
              <span className="break-words">{filterNames[key]}: {describe(key)}</span><X className="h-3.5 w-3.5 shrink-0" />
            </button>
          ))}
          <button type="button" onClick={() => onApply({ ...emptyFilters, years: [] })} className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">Tümünü temizle</button>
        </div>
      )}

      <dialog ref={dialogRef} aria-labelledby="lead-filters-title" onClick={event => { if (event.target === event.currentTarget) dialogRef.current?.close(); }} className="m-auto max-h-[90vh] w-[calc(100%-2rem)] max-w-xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-0 text-gray-900 shadow-2xl backdrop:bg-black/50 dark:border-gray-700 dark:bg-gray-900 dark:text-white">
        <form onSubmit={event => { event.preventDefault(); onApply(draft); dialogRef.current?.close(); }}>
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5 dark:border-gray-800">
            <h2 id="lead-filters-title" className="text-lg font-semibold">Leadleri filtrele</h2>
            <button type="button" onClick={() => dialogRef.current?.close()} aria-label="Filtre penceresini kapat" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
          </div>
          <div className="space-y-5 p-6">
            <div>
              <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">Uygulamak istediğiniz filtreleri seçin, ardından değerlerini belirleyin.</p>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(filterNames) as FilterKey[]).filter(key => key !== 'program' || showProgram).map(key => (
                  <button key={key} type="button" aria-pressed={selected.includes(key)} onClick={() => {
                    if (selected.includes(key)) {
                      setSelected(prev => prev.filter(item => item !== key));
                      setDraft(prev => clearFilter(prev, key));
                    } else setSelected(prev => [...prev, key]);
                  }} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm ${selected.includes(key) ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'}`}>
                    {selected.includes(key) ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}{filterNames[key]}
                  </button>
                ))}
              </div>
            </div>

            {selected.map(key => (
              <div key={key} className="space-y-2">
                {key === 'date' ? (
                  <fieldset>
                    <legend className="mb-2 text-sm font-medium">Oluşturulma tarihi</legend>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="space-y-1 text-sm"> <span>Başlangıç tarihi</span><input type="date" value={draft.createdFrom} max={draft.createdTo || undefined} onChange={event => setDraft(prev => ({ ...prev, createdFrom: event.target.value }))} className={fieldClass} /></label>
                      <label className="space-y-1 text-sm"> <span>Bitiş tarihi</span><input type="date" value={draft.createdTo} min={draft.createdFrom || undefined} onChange={event => setDraft(prev => ({ ...prev, createdTo: event.target.value }))} className={fieldClass} /></label>
                    </div>
                  </fieldset>
                ) : key === 'years' ? (
                  <fieldset>
                    <legend className="mb-2 text-sm font-medium">Kayıt yılı</legend>
                    <div className="flex flex-wrap gap-2">
                      {yearOptions.map(year => <label key={year} className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700"><input type="checkbox" checked={draft.years.includes(year)} onChange={event => setDraft(prev => ({ ...prev, years: event.target.checked ? [...prev.years, year] : prev.years.filter(item => item !== year) }))} />{year}</label>)}
                    </div>
                  </fieldset>
                ) : (
                  <label className="block space-y-2 text-sm font-medium">
                    <span>{filterNames[key]}</span>
                    <select value={draft[key]} onChange={event => setDraft(prev => ({ ...prev, [key]: event.target.value }))} className={fieldClass}>
                      <option value={key === 'program' ? '' : 'all'}>Tümü</option>
                      {key === 'status' && statusOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                      {key === 'meetingType' && meetingOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                      {key === 'consultant' && profiles.filter(profile => profile.unit_type === 'sales').map(profile => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
                      {key === 'program' && programs.map(program => <option key={program} value={program}>{program}</option>)}
                    </select>
                  </label>
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
            <button type="button" onClick={() => { setDraft({ ...emptyFilters, years: [] }); setSelected([]); }} className="mr-auto text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">Temizle</button>
            <button type="button" onClick={() => dialogRef.current?.close()} className="rounded-lg border border-gray-200 px-4 py-2 text-sm dark:border-gray-700">Vazgeç</button>
            <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Filtreleri uygula</button>
          </div>
        </form>
      </dialog>
    </>
  );
};

export default SalesLeadFilters;
